import React, { useCallback, useEffect, useMemo } from 'react';
import { ProviderSelector } from '@shipstation/connections-ui';
import { DirectCarrierList } from '@shipstation/connections-ui/direct';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { noop } from 'ui-components/default-props/noop';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { ProviderTypes } from './constants';
import { Providers } from '../enums/providers';
import { useActiveSellerFulfillmentProviders } from '../hooks/use-seller-fulfillment-providers';
import { useSellerLabelProviders } from '../hooks/use-seller-label-providers';
import Global from '../store/global-selectors';
import { useCuiApiConfig } from './helpers/use-cui-api-config';
import { useCuiContextProps } from './helpers/use-cui-context-props';
import { getInternalIdFromApiCode } from './helpers/get-internal-id-from-api-code';
import { useCommonCuiProps } from './helpers/use-common-cui-props';
import { useHideStampsConnection } from '../feature-flags/hooks/use-enable-stamps-link';
import { useEnableDirectModeCarriers } from '../feature-flags/hooks/use-enable-direct-mode-carriers';

const stampsProviderIds = [Providers.Stamps.labelProviderId, Providers.ClientStamps.labelProviderId];

export const CuiProviderSelector = ({
  onProviderSelected = noop,
  onRegisterSuccess = noop,
  onRegisterFailure = noop,
  onBack = noop,
  onCancel = noop,
  onError = noop,
  providerType = ProviderTypes.shipping,
}) => {
  const isDirectMode = useEnableDirectModeCarriers();
  const hideStampsConnection = useHideStampsConnection();
  const commonCuiProps = useCommonCuiProps();
  const cuiApiConfig = useCuiApiConfig();
  const cuiContextProps = useCuiContextProps();
  const userIsSysAdmin = useSelector(Global.selectUserisSysAdmin);
  const [loading, setLoading] = useAsyncSafeState(true);
  const [providerList, setProviderList] = useAsyncSafeState([]);
  const sellerProviders = useSellerLabelProviders();
  const sellerFulfillmentProviders = useActiveSellerFulfillmentProviders();
  const onProviderSelectedWithId = useCallback(
    (provider) => {
      onProviderSelected({
        ...provider,
        providerId: getInternalIdFromApiCode(provider.providerApiCode, provider.providerType),
      });
    },
    [onProviderSelected],
  );

  useEffect(() => {
    axios
      .get('/provider/availableProviders')
      .then((response) => {
        let providers = providerType === ProviderTypes.shipping ? response.data.providers : response.data.fulfillmentProviders;
        providers = providers.map((provider) => {
          const providerCollection = providerType === ProviderTypes.shipping ? sellerProviders : sellerFulfillmentProviders;
          const idProp = providerType === ProviderTypes.shipping ? 'labelProviderId' : 'fulfillmentProviderId';
          const matchingProvider = providerCollection.find((p) => p[idProp] === provider.providerId && !p.requiresSetup);
          return {
            ...provider,
            alreadyConnected: matchingProvider !== undefined,
            providerApiCode: provider.apiCode,
            providerType,
          };
        });

        const providersToDisplay =
          hideStampsConnection && providerType === ProviderTypes.shipping
            ? providers.filter((provider) => !stampsProviderIds.includes(provider.providerId))
            : providers;

        setProviderList(providersToDisplay);
      })
      .catch((error) => {
        onError(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    setLoading,
    setProviderList,
    sellerProviders,
    sellerFulfillmentProviders,
    userIsSysAdmin,
    onError,
    providerType,
    hideStampsConnection,
  ]);

  const directContextProps = useMemo(
    () => ({
      ...cuiContextProps,
      providerList,
      showSeeServices: false,
      onProviderSelected: onProviderSelectedWithId,
      onRegisterSuccess,
      onRegisterFailure,
      onRegisterSubmitRedirectForm: onRegisterSuccess, // used by canada post
      onBack,
      onCancel,
      onError,
    }),
    [cuiContextProps, providerList, onProviderSelectedWithId, onRegisterSuccess, onRegisterFailure, onBack, onCancel, onError],
  );

  if (loading) {
    return <LoadingIndicator />;
  }

  if (isDirectMode) {
    return <DirectCarrierList apiConfig={cuiApiConfig} contextProps={directContextProps} />;
  }

  return (
    <ProviderSelector {...commonCuiProps} onError={onError} onProviderSelected={onProviderSelectedWithId} providerList={providerList} />
  );
};

CuiProviderSelector.propTypes = {
  onProviderSelected: PropTypes.func,
  onRegisterSuccess: PropTypes.func,
  onRegisterFailure: PropTypes.func,
  onBack: PropTypes.func,
  onCancel: PropTypes.func,
  onError: PropTypes.func,
  providerType: PropTypes.oneOf(Object.values(ProviderTypes)),
};
